/* eslint-disable no-dupe-class-members */ // Linter doesn't understand private method notation
import { SasTokenLocations } from '@/utils/LocalStorageActor'
import { sasNeedsRefresh, createResourceUrl } from '@/utils/SasHelpers'
import ApiServer from '@/utils/api/ApiServer'
import { BlobServiceClient } from '@azure/storage-blob'
import i18n from '@/i18n'
import store from '@/store/index.js'
import { saveAs } from 'file-saver'
import { startCase } from 'lodash'
import { getBlobByFileName } from '@/utils/storage/BlobStorageHelpers'
import moment from 'moment'

const endpoint = 'companyinfo/blobinfo/tractshapes'
const GEOJSON_FILE_NAME = 'angelGeojson.json'
export const GEOJSON_FILE_TYPE = 'application/json'

const CREATED_STATUS = 201

export const GeojsonType = {
  Feature: {
    id: 0,
    label: 'Feature'
  },
  FeatureCollection: {
    id: 1,
    label: 'FeatureCollection'
  },
  AngelGeojson: {
    id: 2,
    label: 'AngelGeojson'
  }
}

export function geojsonIs (geojson, geojsonGeometryType) {
  return typeFromGeojson(geojson)?.id === geojsonGeometryType.id
}

export function geojsonIsNot (geojson, geojsonGeometryType) {
  return typeFromGeojson(geojson)?.id !== geojsonGeometryType.id
}

function typeFromGeojson (geojson) {
  const type = geojson?.type
  if (type) return GeojsonType[type]
  return undefined
}

export const GeojsonGeometryType = {
  Point: {
    id: 0,
    label: 'Point'
  },
  LineString: {
    id: 1,
    label: 'LineString'
  },
  Polygon: {
    id: 2,
    label: 'Polygon'
  },
  MultiPoint: {
    id: 3,
    label: 'MultiPoint'
  },
  MultiLineString: {
    id: 4,
    label: 'MultiLineString'
  },
  MultiPolygon: {
    id: 5,
    label: 'MultiPolygon'
  }
}

export function flattenedCoordinatesFromPolygon (polygon) {
  if (GeojsonGeometryType[polygon.geometry.type] === GeojsonGeometryType.Polygon) {
    const coordinates = polygon?.geometry?.coordinates[0]
    if (coordinates) return coordinates
  }
  return undefined
}

export const AngelGeojsonLayer = { // Each of our "layers" is a FeatureCollection
  Cut: 'cut',
  Parcels: 'parcels'
}

export class AngelGeojson {
  type = GeojsonType.AngelGeojson.label
  cut = { angelGeojsonLayer: AngelGeojsonLayer.Cut }
  parcels = { angelGeojsonLayer: AngelGeojsonLayer.Parcels }

  constructor (geojson) {
    if (!this.#validateGeojson(geojson)) return
    geojson = this.#handleArray(geojson)

    if (geojsonIs(geojson, GeojsonType.AngelGeojson)) {
      this.#createFromAngelGeojson(geojson)
    } else

    if (geojsonIs(geojson, GeojsonType.FeatureCollection)) {
      this.#createFromFeatureCollection(geojson)
    } else

    if (geojsonIs(geojson, GeojsonType.Feature)) {
      this.#createFromFeature(geojson)
    }

    this.#assignAngelGeojsonLayerAttributes()
  }

  #validateGeojson (geojson) {
    if (geojson?.type === 'GeometryCollection') {
      store.dispatch('snackbar/setSnackError', i18n.t('geometryCollectionNotSupported'))
      return false
    }
    return true
  }

  #handleArray (geojson) {
    if (Array.isArray(geojson)) {
      return geojson.find(i => geojsonIs(i, GeojsonType.FeatureCollection) || geojsonIs(i, GeojsonType.Feature))
    }
    return geojson
  }

  #createFromAngelGeojson (geojson) {
    this.cut = geojson?.cut
    this.parcels = geojson?.parcels
  }

  #createFromFeatureCollection (geojson) {
    if (geojson.angelGeojsonLayer === AngelGeojsonLayer.Parcels) {
      this.parcels = geojson
    } else {
      this.cut = geojson
    }
  }

  #createFromFeature (geojson) {
    this.cut = {
      type: GeojsonType.FeatureCollection.label,
      features: [geojson]
    }
  }

  #assignAngelGeojsonLayerAttributes () {
    this.cut.angelGeojsonLayer = AngelGeojsonLayer.Cut
    this.parcels.angelGeojsonLayer = AngelGeojsonLayer.Parcels
  }

  setLayer (layer, value) {
    switch (layer) {
      case AngelGeojsonLayer.Cut: {
        const content = value
        this[AngelGeojsonLayer.Cut] = {
          ...content,
          angelGeojsonLayer: AngelGeojsonLayer.Cut
        }
        break
      }
      case AngelGeojsonLayer.Parcels: {
        const content = value
        this[AngelGeojsonLayer.Parcels] = {
          ...content,
          angelGeojsonLayer: AngelGeojsonLayer.Parcels
        }
        break
      }
    }
  }

  getLayer (layer) {
    switch (layer) {
      case AngelGeojsonLayer.Cut: {
        return this[AngelGeojsonLayer.Cut]
      }
      case AngelGeojsonLayer.Parcels: {
        return this[AngelGeojsonLayer.Parcels]
      }
    }
  }

  getFeatures (layer) {
    return this.getLayer(layer)?.features
  }

  getFirstFeature (layer) {
    return this.getFeatures(layer)[0]
  }

  layerIsEmpty (layer) {
    return !this.layerIsNotEmpty(layer)
  }

  layerIsNotEmpty (layer) {
    return this.getFeatures(layer)?.length > 0
  }

  download (tractName) {
    if (tractName == null || tractName === '') {
      tractName = i18n.t('untitled')
    }
    const json = {
      type: this.type,
      cut: this.cut,
      parcels: this.parcels
    }
    const blob = new Blob([JSON.stringify(json, null, '  ')], { type: GEOJSON_FILE_TYPE })
    saveAs(blob, `${tractName.replaceAll(/\s/g, '_').replaceAll(/[<>:"/\\|?*.]+/g, '_')}_GeographicData_${moment().format('YYYY-MM-DD')}.json`)
  }

  downloadLayer (tractName, layer) {
    if (tractName == null || tractName === '') {
      tractName = i18n.t('untitled')
    }
    const geojson = this.getLayer(layer)
    const blob = getGeojsonBlob(geojson)
    saveAs(blob, `${tractName.replaceAll(/\s/g, '_').replaceAll(/[<>:"/\\|?*.]+/g, '_')}_${startCase(layer)}_${moment().format('YYYY-MM-DD')}.json`)
  }
}

function getGeojsonBlob (geojson) {
  return new Blob([JSON.stringify(geojson, null, '  ')], { type: GEOJSON_FILE_TYPE })
}

const INVALID_TRACT_ID = -1
class AngelGeojsonClient {
  geojsonData = new Map()
  loading = false

  #cleanTractId (tractId) {
    const tractIdInt = parseInt(tractId)
    if (!!tractIdInt && tractIdInt > 0) return tractIdInt
    return INVALID_TRACT_ID
  }

  #isFalsyTractId (tractId) {
    return this.#cleanTractId(tractId) === INVALID_TRACT_ID
  }

  async getContainerClient () {
    await this.initConnection()
    return this.containerClient
  }

  async initConnection () {
    const sasCapsule = JSON.parse(localStorage.getItem(SasTokenLocations.SHAPE_FILES_SAS))
    if (sasCapsule === null || sasNeedsRefresh(sasCapsule.data[0].blobSasUri)) {
      this.loading = true
      const response = await ApiServer.get(ApiServer.urlFor(endpoint))
      this.loading = false
      localStorage.setItem(SasTokenLocations.SHAPE_FILES_SAS, JSON.stringify(response))
      this.containerClient = undefined
    }

    if (this.containerClient === undefined) {
      const storedResponse = JSON.parse(localStorage.getItem(SasTokenLocations.SHAPE_FILES_SAS))
      this.container = storedResponse.data[0].container
      this.location = storedResponse.data[0].location
      this.client = new BlobServiceClient(storedResponse.data[0].blobSasUri)
      this.containerClient = this.client.getContainerClient(this.container)
    }
  }

  getBlobLocation (tractId) {
    return `${this.location}/${tractId}/`
  }

  async #getGeojsonUrl (tractId) {
    try {
      const containerClient = await this.getContainerClient()
      const blobLocation = this.getBlobLocation(tractId)
      const blobs = containerClient.listBlobsByHierarchy('/', { prefix: blobLocation })
      const blobItem = await getBlobByFileName(blobs, GEOJSON_FILE_NAME)

      const blobUrl = createResourceUrl(
        JSON.parse(localStorage.getItem(SasTokenLocations.SHAPE_FILES_SAS))
          .data[0].blobSasUri,
        this.container,
        blobItem.value.name
      )
      return blobUrl.href
    } catch (e) {
      if (e.name === 'TypeError') {
        throw new ReferenceError('No geoJSON for tract #' + tractId)
      }
      if (e.code !== 'AuthorizationFailure') {
        store.dispatch('snackbar/setSnackError', i18n.t('getGeojsonFailure', {
          tractName: store.getters['tract/allTracts'].find(t => t.tractId === tractId).name ?? ''
        }))
      }
      throw e
    }
  }

  async readGeojson (tractId, forceFetch = false) {
    if (this.#isFalsyTractId(tractId)) {
      return null
    }

    const existingData = this.geojsonData.get(tractId)
    if (existingData && !forceFetch) return existingData

    try {
      const geoJsonUrl = await angelGeojsonClient.#getGeojsonUrl(tractId)
      const geoJsonResponse = await fetch(geoJsonUrl)
      const geoJsonBlob = await geoJsonResponse.blob()
      const angelGeojson = await readGeojsonFile(geoJsonBlob)

      this.geojsonData.set(tractId, angelGeojson)
      return angelGeojson
    } catch (e) {
      return null
    }
  }

  // Resolves true on success, false on failure (errors are reported via snackbar, not thrown).
  async uploadGeojson (tractId, geojson) {
    this.loading = true
    try {
      const angelGeojson = new AngelGeojson(geojson)
      var geojsonStringified = JSON.stringify(angelGeojson, undefined, 2)
      const geojsonFile = new File([geojsonStringified], GEOJSON_FILE_NAME, { type: GEOJSON_FILE_TYPE })
      await this.#uploadGeojsonFile(tractId, geojsonFile)
      this.geojsonData.set(tractId, geojson)
      return true
    } catch (e) {
      console.error(e)
      if (e.code !== 'AuthorizationFailure') {
        store.dispatch('snackbar/setSnackError',
          i18n.t('uploadGeojsonFailure', {
            tractName: store.getters['tract/allTracts']
              .find(t => t.tractId === tractId)?.name ?? i18n.t('unknownTract')
          }))
      }
      return false
    } finally {
      this.loading = false
    }
  }

  async #uploadGeojsonFile (tractId, geojsonFile) {
    try {
      let exisitingGeojson = null
      exisitingGeojson = await this.#getGeojsonUrl(tractId)
      if (exisitingGeojson) await this.#deleteFile(tractId, exisitingGeojson)
      this.geojsonData.delete(tractId)
    } catch (e) { /* No GeoJSON for tract */ }
    try {
      this.loading = true
      const containerClient = await this.getContainerClient()
      const fullBlobLocation = await this.getBlobLocation(tractId) + geojsonFile.name
      const blockBlobClient = await containerClient.getBlockBlobClient(fullBlobLocation)
      const options = {
        blobHTTPHeaders: {
          blobContentType: GEOJSON_FILE_TYPE
        }
      }
      await blockBlobClient.uploadData(geojsonFile, options).then(result => {
        const requestStatus = result._response.status
        if (requestStatus === CREATED_STATUS) store.dispatch('snackbar/setSnack', i18n.t('uploadGeojsonSuccess'))
      })
    } catch (e) {
      console.error(e)
      if (e.code !== 'AuthorizationFailure') {
        store.dispatch('snackbar/setSnackError',
          i18n.t('uploadGeojsonFailure', {
            tractName: store.getters['tract/allTracts']
              .find(t => t.tractId === tractId)?.name ?? i18n.t('unknownTract')
          }))
      }
      throw e
    } finally {
      this.loading = false
    }
  }

  async #deleteFile (tractId) {
    try {
      this.loading = true
      await ApiServer.delete(ApiServer.urlFor(`tracts/${tractId}/shape/${GEOJSON_FILE_NAME}`))
    } catch (e) {
      store.dispatch('snackbar/setSnackError', i18n.t('deleteGeojsonFailure', {
        tractName: store.getters['tract/allTracts']
          .find(t => t.tractId === tractId)?.name ?? this.$t('tract')
      }))
      throw e
    } finally {
      this.geojsonData.delete(tractId)
      this.loading = false
    }
  }
}

export const angelGeojsonClient = new AngelGeojsonClient()

export async function readGeojsonFile (geojsonFile) {
  const result = await new Promise((resolve, reject) => {
    var fr = new FileReader()
    fr.onerror = () => {
      fr.abort()
      reject(new DOMException('No GeoJSON found'))
    }
    fr.onload = () => {
      resolve(fr.result)
    }
    fr.readAsText(geojsonFile)
  })

  const resultJson = JSON.parse(result)
  const angelGeojson = new AngelGeojson(resultJson)
  return angelGeojson
}
